import { Trans, useTranslation } from 'react-i18next';

import type {
  CommentTagNotificationData,
  Notification,
} from '@/api/v4/notifications.api';
import { useActionPanelSearchParams } from '@/features/action-panel/use-action-panel-search-params';
import { useNavigateTo } from '@/routes/hooks/use-navigate-to';
import { colors } from '@/theme/colors';
import { Inline } from '@/ui/line/line';
import { SmallText } from '@/ui/typography/widgets';

import {
  NotificationAuthorInitials,
  NotificationCardContainer,
  NotificationHighlight,
} from '../components/notification-card-components';
import { useCommentTagNotificationEntityName } from '../hooks/use-comment-tag-notification-entity-name.query';
import { useMarkNotificationAsRead } from '../hooks/use-mark-notification-as-read.mutation';
import { timeAgo } from '../utils/time-ago';

export const CommentTagNotificationCard = ({
  notification,
}: {
  notification: Notification;
}) => {
  useTranslation('notification');
  const { commenterName, entityTagged, entityId, commentId } =
    notification.data as CommentTagNotificationData;
  const isRead = Boolean(notification?.readAt);
  const entityName = useCommentTagNotificationEntityName({
    entityType: entityTagged,
    entityId,
  });
  const navigateTo = useNavigateTo();
  const { markAsRead } = useMarkNotificationAsRead();
  const { generateActionItemSearchParam } = useActionPanelSearchParams();

  const handleNavigateToNotificationEntityPage = async () => {
    if (!isRead) {
      await markAsRead({ notificationId: notification.id });
    }

    const commentSearchParams = generateActionItemSearchParam(
      commentId,
      'comment',
    );

    if (entityTagged === 'company') {
      return navigateTo.company({ companyId: entityId }, commentSearchParams);
    }

    if (entityTagged === 'project') {
      return navigateTo.project({ projectId: entityId }, commentSearchParams);
    }
  };

  return (
    <NotificationCardContainer
      isRead={isRead}
      justify="space-between"
      alignItems="center"
      onClick={handleNavigateToNotificationEntityPage}
    >
      <Inline gap="12px" alignItems="center">
        <NotificationAuthorInitials
          name={commenterName ?? ''}
          bgColor={isRead ? colors.system.disable : colors.blue.primaryA}
        />
        <SmallText color={colors.gray.c13}>
          <Trans
            i18nKey="taggedYou"
            ns="notification"
            values={{
              commenterName: commenterName ?? '',
              entityName: entityName ?? '',
            }}
            components={{ strong: <NotificationHighlight /> }}
          />
        </SmallText>
      </Inline>
      <SmallText>{timeAgo(notification?.createdAt)}</SmallText>
    </NotificationCardContainer>
  );
};
